import {
  DeleteIcon,
  EditOutlinedIcon,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@cmg/design-system';
import { FormattedNumber } from '@cmg/i18n';
import orderBy from 'lodash/orderBy';
import React from 'react';

import type { FundItem } from '../../FundLevelDemand';
import { DemandItem } from './components/DemandItem';
import { DemandItemTooltip } from './components/DemandItemTooltip';

export type Props = {
  items: FundItem[];
  onEditItem: (fundId: string) => void;
  onDeleteItem: (fundId: string) => void;
  canUpdate: boolean;
};

const FundIoisGrid: React.FC<Props> = ({ items, onEditItem, onDeleteItem, canUpdate }) => {
  const totalAllocation = items.reduce((sum, { allocation }) => sum + (allocation.shares ?? 0), 0);
  const totalPctOfFirmAllocation = items.reduce(
    (sum, { allocation }) => sum + (allocation.pctOfFirmAllocation ?? 0),
    0
  );

  return (
    <TableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Fund</TableCell>
            <TableCell>Demand</TableCell>
            <TableCell align="right">Allocation</TableCell>
            <TableCell align="right">% Of Firm Allocation</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow
            sx={{ background: theme => theme.palette.background.lightBlue }}
            aria-label="Get Total Row"
          >
            <TableCell colSpan={2}>
              <Typography variant="subtitle1">Total</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle1">
                <FormattedNumber value={totalAllocation} format="integer" />
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography fontWeight="bold">
                {!!totalPctOfFirmAllocation && (
                  <FormattedNumber value={totalPctOfFirmAllocation} format="percent" />
                )}
              </Typography>
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
          {items.map(({ fund, allocation, iois }) => {
            const sortedIois = orderBy(iois, ['pricingType'], ['desc']);
            const remaining = sortedIois.slice(1);
            const showMore = remaining.length > 0;

            return (
              <TableRow key={fund.id}>
                <TableCell data-testid="fund-level-name-readonly">{fund.name}</TableCell>
                <TableCell data-testid="fund-level-demand-readonly">
                  <Stack direction="row" spacing={1}>
                    {sortedIois[0] && (
                      <span>
                        <DemandItem ioi={sortedIois[0]} /> {showMore ? '...' : null}
                      </span>
                    )}
                    {showMore ? <DemandItemTooltip iois={sortedIois.slice(1)} /> : null}
                  </Stack>
                </TableCell>
                <TableCell align="right" data-testid="fund-level-allocation-readonly">
                  {!!allocation.shares && (
                    <FormattedNumber value={allocation.shares} format="integer" />
                  )}
                </TableCell>
                <TableCell align="right" data-testid="fund-level-firm-percentage-readonly">
                  <FormattedNumber value={allocation.pctOfFirmAllocation} format="percent" />
                </TableCell>
                <TableCell>
                  {canUpdate && (
                    <Stack direction="row" justifyContent="space-evenly">
                      <IconButton size="small" onClick={() => onEditItem(fund.id)}>
                        <EditOutlinedIcon color="primary" />
                      </IconButton>
                      <IconButton size="small" onClick={() => onDeleteItem(fund.id)}>
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FundIoisGrid;
